import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Package,
  Receipt,
  ShoppingCart,
  AlertTriangle,
  FileText,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';

import { useAuth } from '../providers/AuthProvider';
import {
  useDashboardStats,
  useSalesTrend,
  useLowStockAlerts,
  useRecentTransactions,
  useRealtimeUpdates,
  useConnectionStatus,
} from '../hooks';
import {
  StatCard,
  SalesChart,
  LowStockAlerts,
  RecentTransactions,
  QuickActions,
  ConnectionStatus,
} from '../components/dashboard';

export default function DashboardPage() {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Chart period state
  const [chartPeriod, setChartPeriod] = useState<'week' | 'month'>('week');

  // Realtime updates
  const { connectionStatus } = useRealtimeUpdates({
    showNotifications: true,
    autoRefresh: true,
  });

  // Connection status with retry logic
  const { canRetry, reconnect: handleReconnect } = useConnectionStatus();

  // Data fetching
  const {
    data: stats,
    isLoading: isLoadingStats,
    isRefetching: isRefetchingStats,
  } = useDashboardStats();

  const {
    data: salesTrend,
    isLoading: isLoadingSalesTrend,
  } = useSalesTrend(chartPeriod);

  const {
    data: lowStockData,
    isLoading: isLoadingLowStock,
    isRefetching: isRefetchingLowStock,
    refetch: refetchLowStock,
  } = useLowStockAlerts(10);

  const {
    data: recentTransactions,
    isLoading: isLoadingTransactions,
    isRefetching: isRefetchingTransactions,
    refetch: refetchTransactions,
  } = useRecentTransactions(10);

  // Format currency
  const formatCurrency = useCallback(
    (value: number) => {
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: 'EGP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },
    [i18n.language]
  );

  // Format number with locale
  const formatNumber = useCallback(
    (value: number) => {
      return new Intl.NumberFormat(i18n.language).format(value);
    },
    [i18n.language]
  );

  // Refresh all dashboard data
  const handleRefreshAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }, [queryClient]);

  // Stat cards configuration
  const statCards = [
    {
      key: 'todaySales',
      title: t('stats.todaySales'),
      value: isLoadingStats
        ? '-'
        : formatCurrency(stats?.todaySales.amount ?? 0),
      subtitle: isLoadingStats
        ? undefined
        : t('stats.transactionCount', { count: stats?.todaySales.count ?? 0 }),
      icon: Receipt,
      color: 'green' as const,
      trend: stats?.monthlyGrowth
        ? { value: stats.monthlyGrowth, isPositive: stats.monthlyGrowth > 0 }
        : undefined,
    },
    {
      key: 'todayPurchases',
      title: t('stats.todayPurchases'),
      value: isLoadingStats
        ? '-'
        : formatCurrency(stats?.todayPurchases.amount ?? 0),
      subtitle: isLoadingStats
        ? undefined
        : t('stats.transactionCount', { count: stats?.todayPurchases.count ?? 0 }),
      icon: ShoppingCart,
      color: 'blue' as const,
    },
    {
      key: 'totalProducts',
      title: t('stats.totalItems'),
      value: isLoadingStats ? '-' : formatNumber(stats?.totalProducts ?? 0),
      icon: Package,
      color: 'purple' as const,
    },
    {
      key: 'lowStock',
      title: t('stats.lowStock'),
      value: isLoadingStats ? '-' : formatNumber(stats?.lowStockCount ?? 0),
      subtitle: isLoadingStats
        ? undefined
        : stats?.outOfStockCount
        ? t('stats.outOfStockCount', { count: stats.outOfStockCount })
        : undefined,
      icon: AlertTriangle,
      color: (stats?.lowStockCount ?? 0) > 0 ? 'yellow' as const : 'green' as const,
    },
    {
      key: 'pendingInvoices',
      title: t('stats.pendingInvoices'),
      value: isLoadingStats ? '-' : formatNumber(stats?.pendingInvoices ?? 0),
      icon: FileText,
      color: 'orange' as const,
    },
    {
      key: 'monthlyRevenue',
      title: t('stats.monthlyRevenue'),
      value: isLoadingStats
        ? '-'
        : formatCurrency(stats?.monthlyRevenue ?? 0),
      icon: TrendingUp,
      color: 'teal' as const,
      trend: stats?.monthlyGrowth
        ? { value: stats.monthlyGrowth, isPositive: stats.monthlyGrowth > 0 }
        : undefined,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Connection status banner - only show when not connected */}
      {connectionStatus !== 'connected' && (
        <ConnectionStatus
          status={connectionStatus}
          onReconnect={handleReconnect}
          canRetry={canRetry}
          variant="banner"
        />
      )}

      {/* Welcome header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              {t('welcome', { name: user?.firstName || '' })}
            </h1>
            <p className="mt-1 text-primary-100">{t('overview')}</p>
          </div>

          <div className="flex items-center gap-3">
            {/* Connection status badge */}
            <ConnectionStatus
              status={connectionStatus}
              onReconnect={handleReconnect}
              canRetry={canRetry}
              variant="badge"
              className="bg-white/10 text-white"
            />

            {/* Refresh button */}
            <button
              type="button"
              onClick={handleRefreshAll}
              disabled={isRefetchingStats}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors disabled:opacity-50"
              aria-label={t('common:refresh')}
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefetchingStats ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
              <span className="hidden sm:inline">{t('common:refresh')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {statCards.map((stat) => (
          <StatCard
            key={stat.key}
            title={stat.title}
            value={stat.value}
            subtitle={stat.subtitle}
            icon={stat.icon}
            color={stat.color}
            trend={stat.trend}
            isLoading={isLoadingStats}
          />
        ))}
      </div>

      {/* Quick actions */}
      <QuickActions />

      {/* Sales chart - full width */}
      <SalesChart
        data={salesTrend}
        isLoading={isLoadingSalesTrend}
        period={chartPeriod}
        onPeriodChange={setChartPeriod}
      />

      {/* Low stock alerts and recent transactions - side by side */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <LowStockAlerts
          data={lowStockData?.data}
          total={lowStockData?.total ?? 0}
          isLoading={isLoadingLowStock}
          isRefetching={isRefetchingLowStock}
          onRefresh={() => refetchLowStock()}
          className="min-h-[400px]"
        />

        <RecentTransactions
          data={recentTransactions?.data}
          total={recentTransactions?.total ?? 0}
          isLoading={isLoadingTransactions}
          isRefetching={isRefetchingTransactions}
          onRefresh={() => refetchTransactions()}
          className="min-h-[400px]"
        />
      </div>
    </div>
  );
}
